// project import
import { NavItemType } from 'types/menu';
import {
  getGeneralGroup,
  getTcCopierGroup,
  getTradingToolsGroup,
  getTcAnalyzerGroup,
  feedbackGroup,
  getEaConnectGroup
} from './menu-items';
import useAuth from '../hooks/useAuth';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { canSeeMcp } from '../utils/mcp';
import { canSeeEaConnect } from '../utils/eaConnect';
import { BOTTOM_GROUPS, withNewChips } from './catalog';
import { useNewMenuIds } from 'features/announcements/hooks/useAnnouncements';

// ==============================|| MENU ITEMS ||============================== //

const useMenuItems = (): { items: NavItemType[] } => {
  const { user } = useAuth();
  const hasAnalyzerRole = user?.roles?.includes('Analyzer') || false;
  // AI Connect is concealed until launch; only "MCP User" role-holders see it.
  const canSeeAiConnect = canSeeMcp(user?.roles);
  // EA Connect is concealed again: only "EA Connect User" role-holders see the
  // group. EaConnectGuard gates the routes on the same predicate, so hiding
  // the item here is not the whole gate.
  const showEaConnect = canSeeEaConnect(user?.roles);

  // Reads the SAME predicate as the dashboard's get-started gate. Locking one
  // surface and not the other is the split-brain this replaces: accounts with
  // Free billing saw a locked dashboard next to an unlocked sidebar.
  //
  // isEntitled covers billing OR complimentary access ('Paid Member' held
  // without a subscription — staff and comped accounts, whose limits are set
  // by hand). Note this role is NOT granted at signup: user-manager assigns
  // it only on subscription activation and strips it on cancellation.
  //
  // Locking still requires billing to have ANSWERED. `!isEntitled` alone
  // would be true while listSubscriptions is in flight and flash padlocks at
  // a paying subscriber, so an unresolved isPaid (null) locks nothing.
  const { isPaid, isEntitled } = useSubscriptionStatus();
  const isFreeUser = isPaid === false && !isEntitled;
  // What's New: a NEW chip on each item a recent post announces.
  const newIds = useNewMenuIds();

  // If user has Analyzer role, show TC ANALYZER before TRADING TOOLS
  // If not, show it after TRADING TOOLS
  const items = (
    hasAnalyzerRole
      ? [
          getGeneralGroup(isFreeUser),
          getTcCopierGroup(isFreeUser),
          showEaConnect ? getEaConnectGroup() : null,
          getTcAnalyzerGroup(hasAnalyzerRole),
          getTradingToolsGroup(isFreeUser, canSeeAiConnect),
          feedbackGroup
        ]
      : [
          getGeneralGroup(isFreeUser),
          getTcCopierGroup(isFreeUser),
          showEaConnect ? getEaConnectGroup() : null,
          getTradingToolsGroup(isFreeUser, canSeeAiConnect),
          getTcAnalyzerGroup(hasAnalyzerRole),
          feedbackGroup
        ]
  ).filter(Boolean) as NavItemType[];

  return { items: withNewChips(items, newIds) };
};

// Bottom menu items (Growth & Monetization), with What's New chips like the main menu.
const useBottomMenuItems = (): { items: NavItemType[] } => {
  const newIds = useNewMenuIds();
  return { items: withNewChips(BOTTOM_GROUPS, newIds) };
};

export default useMenuItems;
export { useBottomMenuItems };
